import { addChatMessage } from '../chat/chat.js'; // Correct path: up one level, then into chat

// --- 模块作用域变量 ---
let partialEditSection = null;
let partialEditForm = null;
let confirmBtn = null;
let cancelBtn = null;

let temporarySubscriptionData = null; // Store the initial data when form is shown
let onConfirmCallback = async (finalData) => { console.warn('[Form UI] onConfirmCallback not implemented.', finalData); };
let onCancelCallback = () => { console.warn('[Form UI] onCancelCallback not implemented.'); };

// --- DOM & Helper Functions ---

/**
 * 获取并缓存部分编辑表单相关的 DOM 元素。
 * @returns {boolean} 如果所有必要元素都找到则返回 true。
 */
function _getFormDOMElements() {
    partialEditSection = document.getElementById('partial-edit-section');
    partialEditForm = document.getElementById('partial-edit-form');
    confirmBtn = document.getElementById('confirm-save-btn');
    cancelBtn = document.getElementById('cancel-save-btn');

    if (!partialEditSection || !partialEditForm || !confirmBtn || !cancelBtn) {
        console.error('[Form UI] Essential partial edit form elements not found!');
        return false;
    }
    return true;
}

/**
 * 内部处理函数：当用户点击确认保存按钮时触发。
 * @param {Event} event
 */
async function _handleConfirm(event) {
    event.preventDefault(); // Prevent default form submission
    if (!temporarySubscriptionData) {
        console.error('[Form UI] Cannot confirm save, temporary data is missing.');
        return;
    }

    // 1. Collect and Validate Data
    const formData = new FormData(partialEditForm);
    const finalData = { ...temporarySubscriptionData }; // Start with the initial data
    let isValid = true;
    let firstInvalidField = null;

    // Clear previous validation errors
    partialEditForm.querySelectorAll('.invalid-input').forEach(el => el.classList.remove('invalid-input'));

    ['name', 'amount', 'currency', 'nextBillingDate', 'paymentCycle'].forEach(field => {
        const inputElement = partialEditForm.querySelector(`[name="${field}"]`); // Use querySelector on form
        let value = formData.get(field)?.trim();

        if (value === '' || value === null || value === undefined) {
            finalData[field] = null;
        } else if (field === 'amount') {
            const numValue = parseFloat(value);
            if (isNaN(numValue) || numValue < 0) { // Also check for negative amounts
                 finalData[field] = null;
                 isValid = false;
                 inputElement?.classList.add('invalid-input');
                 if (!firstInvalidField) firstInvalidField = inputElement;
            } else {
                 finalData[field] = numValue;
            }
        } else if (field === 'nextBillingDate') {
            // Basic date format validation (YYYY-MM-DD) - browser input[date] helps
            if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                finalData[field] = null; // Or keep original if needed?
                 // isValid = false; // Browser usually enforces format
                 // inputElement?.classList.add('invalid-input');
                 // if (!firstInvalidField) firstInvalidField = inputElement;
                 console.warn(`[Form UI] Invalid date format detected for ${field}: ${value}`);
                 finalData[field] = value; // Keep value as is for now
            } else {
                finalData[field] = value;
            }
        } else {
            finalData[field] = value;
        }

        // Required Field Validation (Name)
        if (field === 'name' && !finalData[field]) {
            isValid = false;
            inputElement?.classList.add('invalid-input');
            if (!firstInvalidField) firstInvalidField = inputElement;
        }
    });

    if (!isValid) {
        addChatMessage('请检查表单内容：服务名称不能为空，金额必须是有效的非负数字。' , 'system');
        firstInvalidField?.focus();
        return;
    }

    console.log('[Form UI] Sending confirmed data via callback:', finalData);

    // 2. Disable Buttons and Show Loading
    const originalBtnText = confirmBtn.textContent;
    confirmBtn.disabled = true;
    confirmBtn.textContent = '正在保存...';
    cancelBtn.disabled = true;

    // 3. Call the confirm callback
    try {
        await onConfirmCallback(finalData);
        // Success: hide form (callback should handle adding card etc.)
        hidePartialEditForm(); // Hide on success
        addChatMessage('订阅信息已补充并成功保存。' , 'assistant');
    } catch (error) {
        console.error('[Form UI] Error executing onConfirmCallback:', error);
        addChatMessage(`保存失败: ${error.message}`, 'system');
        // Keep form visible on error for user to retry or adjust
    } finally {
        // 4. Re-enable Buttons
        confirmBtn.disabled = false;
        confirmBtn.textContent = originalBtnText || '确认保存';
        cancelBtn.disabled = false;
    }
}

/**
 * 内部处理函数：当用户点击取消按钮时触发。
 */
function _handleCancel() {
    console.log("[Form UI] Partial save cancelled by user.");
    hidePartialEditForm();
    onCancelCallback(); // Call the cancel callback
    addChatMessage('操作已取消。', 'system');
    // Optional: Clear system message after delay (could be in common UI module later)
    // setTimeout(() => { ... remove message ... }, 2000);
}

// --- Public API ---

/**
 * Initializes the partial edit form module.
 * @param {object} options
 * @param {function(finalData: object): Promise<void>} options.onConfirm - Callback when user confirms.
 * @param {function(): void} options.onCancel - Callback when user cancels.
 */
export function initForm({ onConfirm, onCancel }) {
    console.log('[Form UI] Initializing...');
    if (!_getFormDOMElements()) {
        return; // Stop initialization if elements are missing
    }

    onConfirmCallback = onConfirm || onConfirmCallback;
    onCancelCallback = onCancel || onCancelCallback;

    // Attach listeners (remove first to prevent duplicates if called again)
    confirmBtn.removeEventListener('click', _handleConfirm);
    cancelBtn.removeEventListener('click', _handleCancel);
    confirmBtn.addEventListener('click', _handleConfirm);
    cancelBtn.addEventListener('click', _handleCancel);

    console.log('[Form UI] Initialization complete.');
}

/**
 * Shows the form for confirming/completing partially parsed data.
 * @param {object} initialData - The partially parsed data.
 * @param {Array<string>} [missingFields=[]] - List of fields identified as missing.
 */
export function showPartialEditForm(initialData, missingFields = []) {
    if (!partialEditSection || !partialEditForm) {
        console.error("[Form UI] Cannot show form: elements not found!");
        return;
    }

    temporarySubscriptionData = initialData; // Store data for confirmation handler
    partialEditForm.innerHTML = ''; // Clear previous content

    const fields = ['name', 'amount', 'currency', 'nextBillingDate', 'paymentCycle'];
    const fieldLabels = { name: '服务名称', amount: '金额', currency: '币种', nextBillingDate: '下次扣费日期', paymentCycle: '付款周期' };

    // Create form elements dynamically
    fields.forEach(field => {
        const isMissing = missingFields.includes(field);
        const currentValue = initialData[field] ?? '';

        const label = document.createElement('label');
        label.htmlFor = `partial-${field}`;
        label.textContent = `${fieldLabels[field] || field}${isMissing ? '*' : ''}:`; // Mark missing
        label.classList.add('partial-edit-label');
        if (isMissing) label.classList.add('missing');

        const input = document.createElement('input');
        input.type = (field === 'amount') ? 'number' : (field === 'nextBillingDate') ? 'date' : 'text';
        if (field === 'amount') {
            input.step = 'any';
            input.min = '0'; // Prevent negative numbers directly in input
        }
        input.id = `partial-${field}`;
        input.name = field;
        input.value = currentValue;
        input.placeholder = isMissing ? '请补充此信息' : '';
        input.classList.add('partial-edit-input');
        if (isMissing) {
             input.classList.add('missing-input');
             input.required = true; // Add basic HTML5 required attribute
        }
        // Mark name as required always
        if (field === 'name') input.required = true;

        partialEditForm.appendChild(label);
        partialEditForm.appendChild(input);
    });

    // Show the section and scroll into view
    partialEditSection.classList.remove('hidden');
    partialEditSection.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Focus the first missing field if any
    const firstMissingInput = partialEditForm.querySelector('.missing-input');
    firstMissingInput?.focus();
}

/**
 * Hides the partial edit form and clears temporary data.
 */
export function hidePartialEditForm() {
    if (partialEditSection) {
        partialEditSection.classList.add('hidden');
        if (partialEditForm) partialEditForm.innerHTML = ''; // Clear form fields
    }
    temporarySubscriptionData = null; // Clear the stored data
    console.log("[Form UI] Partial edit form hidden and temporary data cleared.");
}
